/**
 * Modern Storefront tab configuration.
 * Admin-configurable bottom tab bar (label, icon, enabled, order, default).
 * Visitor's last active tab persists for the browser session only.
 *
 * `_rev` is bumped on every mutation so React subscribers re-render on cloud
 * load/realtime updates. `reload()` rehydrates from localStorage (used by
 * cloudSync after pulling fresh app_data).
 */
import { create } from "zustand";

const KEY = "khays_modern_tabs";
const SESSION_KEY = "khays_modern_active_tab";

export type ModernTabId =
  | "home" | "available" | "offers" | "search" | "track" | "swap" | "settings"
  | "favorites" | "cart" | "profile";

export type IconName =
  | "Home" | "Store" | "Tag" | "Search" | "Truck" | "ArrowLeftRight" | "Settings"
  | "ShoppingBag" | "Package" | "Sparkles" | "Compass" | "User"
  | "Heart" | "ShoppingCart";

export interface ModernTab {
  id: ModernTabId;
  label: string;
  icon: IconName;
  enabled: boolean;
  order: number;
  /** Optional custom icon (URL or data URL). When set, overrides the lucide `icon` field. */
  customIconUrl?: string;
}

export interface ModernTabsConfig {
  tabs: ModernTab[];
  defaultTabId: ModernTabId;
}

const DEFAULTS: ModernTabsConfig = {
  defaultTabId: "home",
  tabs: [
    { id: "home",      label: "Home",        icon: "Home",            enabled: true,  order: 0 },
    { id: "available", label: "In Stock",    icon: "Store",           enabled: true,  order: 1 },
    { id: "offers",    label: "Offers",      icon: "Tag",             enabled: true,  order: 2 },
    { id: "search",    label: "Search",      icon: "Search",          enabled: true,  order: 3 },
    { id: "track",     label: "Track",       icon: "Truck",           enabled: true,  order: 4 },
    { id: "swap",      label: "Swap",        icon: "ArrowLeftRight",  enabled: true,  order: 5 },
    { id: "favorites", label: "Favorites",   icon: "Heart",           enabled: false, order: 6 },
    { id: "cart",      label: "Cart",        icon: "ShoppingCart",    enabled: false, order: 7 },
    { id: "profile",   label: "Profile",     icon: "User",            enabled: false, order: 8 },
    { id: "settings",  label: "Settings",    icon: "Settings",        enabled: true,  order: 9 },
  ],
};

function load(): ModernTabsConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw);
      const byId = new Map<ModernTabId, ModernTab>();
      for (const t of (p.tabs || [])) byId.set(t.id, t);
      const merged = DEFAULTS.tabs.map(d => byId.get(d.id) ? { ...d, ...byId.get(d.id)! } : d);
      return {
        tabs: merged.sort((a, b) => a.order - b.order),
        defaultTabId: p.defaultTabId || DEFAULTS.defaultTabId,
      };
    }
  } catch {}
  return DEFAULTS;
}

function persist(cfg: ModernTabsConfig) {
  try { localStorage.setItem(KEY, JSON.stringify(cfg)); } catch {}
}

interface State extends ModernTabsConfig {
  _rev: number;
  activeTab: ModernTabId;
  setActiveTab: (id: ModernTabId) => void;
  patchTab: (id: ModernTabId, p: Partial<ModernTab>) => void;
  setDefaultTabId: (id: ModernTabId) => void;
  setTabsOrder: (ids: ModernTabId[]) => void;
  reset: () => void;
  reload: () => void;
}

function readSessionActive(fallback: ModernTabId): ModernTabId {
  try {
    const v = sessionStorage.getItem(SESSION_KEY) as ModernTabId | null;
    if (v) return v;
  } catch {}
  return fallback;
}

export const useModernTabs = create<State>((set, get) => {
  const initial = load();
  return {
    ...initial,
    _rev: 0,
    activeTab: readSessionActive(initial.defaultTabId),
    setActiveTab: (id) => {
      try { sessionStorage.setItem(SESSION_KEY, id); } catch {}
      set({ activeTab: id });
    },
    patchTab: (id, p) => {
      const next = get().tabs.map(t => t.id === id ? { ...t, ...p } : t);
      const cfg: ModernTabsConfig = { tabs: next, defaultTabId: get().defaultTabId };
      persist(cfg);
      set({ tabs: next, _rev: get()._rev + 1 });
    },
    setDefaultTabId: (id) => {
      const cfg: ModernTabsConfig = { tabs: get().tabs, defaultTabId: id };
      persist(cfg);
      set({ defaultTabId: id, _rev: get()._rev + 1 });
    },
    setTabsOrder: (ids) => {
      const map = new Map(get().tabs.map(t => [t.id, t]));
      const next = ids.map((id, i) => ({ ...(map.get(id)!), order: i })).filter(Boolean);
      const cfg: ModernTabsConfig = { tabs: next, defaultTabId: get().defaultTabId };
      persist(cfg);
      set({ tabs: next, _rev: get()._rev + 1 });
    },
    reset: () => {
      try { localStorage.removeItem(KEY); } catch {}
      set({ ...DEFAULTS, _rev: get()._rev + 1, activeTab: readSessionActive(DEFAULTS.defaultTabId) });
    },
    reload: () => {
      const fresh = load();
      set({ ...fresh, _rev: get()._rev + 1 });
    },
  };
});
